import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroRichText,
	CerebroStyledBox,
	CerebroText,
} from '../../../shared/components';
import { useApi, useAuth } from '../../../shared/context';
import { CurrentUser, DataRoomEmail, DataRoomUser } from '../../../shared/models';
import { FormikUtils, HtmlUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import * as Yup from 'yup';

interface SendEmailFormProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null | undefined;
	close: () => void;
}

export const SendEmailForm: React.FC<SendEmailFormProps> = ({ dataRoomGuid, user, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const { currentUser } = useAuth();
	const ref = React.useRef<HTMLDivElement>(null);

	const sendEmailMutation = useMutation(
		async ({ values }: SendEmailFormMutationVariables) => {
			const { data, error } = await getApiResponseData<DataRoomEmail>(
				api.post(CerebroApiEndpoints.DataRoomUsers.Email(dataRoomGuid, user?.guid), {
					...values,
					to: values.to,
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Email was sent.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={user != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<Formik
				validationSchema={SendEmailFormSchema}
				initialValues={{
					to: user?.emailAddress,
					subject: '',
					emailBody: buildDefaultEmailBody(currentUser),
				}}
				onSubmit={FormikUtils.MutateOnSubmit(sendEmailMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm
							flatIndicator
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
							scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel>To</CerebroLabel>
									<CerebroText>
										{values.to} ({user?.firstName} {user?.lastName})
									</CerebroText>
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="subject" required>
										Subject
									</CerebroLabel>
									<Field
										id="subject"
										as={CerebroInput}
										name="subject"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('subject', errors, touched)}
									/>
									{FormikUtils.IsInvalid('subject', errors, touched) && (
										<CerebroText variant="error">{errors.subject}</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="emailBody" required>
										Email Body
									</CerebroLabel>
									<CerebroRichText
										value={values.emailBody ?? ''}
										onChange={updatedValue => setFieldValue('emailBody', updatedValue)}
									/>
									{FormikUtils.IsInvalid('emailBody', errors, touched) && (
										<CerebroText variant="error">{errors.emailBody}</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroStyledBox alignitems="left">
										<CerebroHeading
											size="h2"
											component="h2"
											sx={{ marginTop: -1, marginBottom: 2 }}
										>
											Legend
										</CerebroHeading>
										<CerebroText>#bankerFirstName</CerebroText>
										<CerebroText>#loginLink</CerebroText>
										<CerebroText>#dataRoomLink</CerebroText>
										<CerebroText sx={{ marginTop: 2 }}>
											Use these tags in the email body which will be replaced when the email is
											sent.
										</CerebroText>
									</CerebroStyledBox>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Send Email
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

function buildDefaultEmailBody(currentUser: CurrentUser): string {
	let body: string = '<p>#bankerFirstName,</p><p><br/><br/></p>';

	body += '<p>Thank you</p>';
	body += `<p>${currentUser.firstName} ${currentUser.lastName}</p>`;

	return body;
}

type SendEmailFormMutationVariables = FormikUtils.MutateOnSubmitVariables<SendEmailFormValues>;
interface SendEmailFormValues {
	to: string | undefined;
	subject: string;
	emailBody: string;
}
type SendEmailFormValueErrors = FormikErrors<SendEmailFormValues>;

const SendEmailFormSchema = Yup.object().shape({
	subject: Yup.string().required('subject is required'),
	emailBody: Yup.string().required('email body is required'),
});
